import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const badges = ["100% Natural", "100% Veg", "No Preservatives", "No Added Colours"];

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const rotY = useTransform(sx, [-0.5, 0.5], [-7, 7]);
  const rotX = useTransform(sy, [-0.5, 0.5], [5, -5]);
  const driftX = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const driftY = useTransform(sy, [-0.5, 0.5], [-10, 10]);

  return (
    <section
      ref={ref}
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
      }}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="grain relative flex min-h-[100svh] items-center overflow-hidden pt-16"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute -left-32 top-24 h-96 w-96 rounded-full bg-leaf/15 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-0 h-[28rem] w-[28rem] rounded-full bg-[#CA8A04]/10 blur-3xl" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-4 py-12 sm:px-6 lg:grid-cols-[1.15fr,1fr] lg:gap-6">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-leaf/30 bg-white/70 px-4 py-1.5 backdrop-blur-sm"
          >
            <span className="h-2 w-2 rounded-full bg-leaf" />
            <span className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-forest">Goodness, Naturally</span>
          </motion.div>

          <h1 className="font-display text-[9vw] font-extrabold leading-[1.06] tracking-tight text-forest sm:text-6xl lg:text-7xl xl:text-[5.2rem]">
            <MaskLine delay={0.2}>MEET YOUR NEW</MaskLine>
            <MaskLine delay={0.32}>
              <span className="text-leaf">PANTRY</span> ESSENTIALS.
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.65 }}
            className="mt-6 max-w-md text-base leading-relaxed text-ink/70 sm:text-lg"
          >
            12 single-ingredient powders. 100% nature, 0% nonsense. No chopping, no tears — just sprinkle, stir, done.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.78 }} className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/shop"
              data-testid="hero-shop-cta"
              className="group flex items-center gap-2 rounded-full bg-forest px-7 py-4 text-sm font-extrabold uppercase tracking-wide text-cream shadow-lg transition-all duration-300 hover:bg-leaf hover:shadow-xl active:scale-95"
            >
              Shop the powders
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <button
              data-testid="hero-squad-cta"
              onClick={() => document.getElementById("squad")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full border-2 border-forest/15 bg-white/60 px-7 py-4 text-sm font-extrabold uppercase tracking-wide text-forest backdrop-blur-sm transition-all duration-300 hover:border-forest hover:bg-forest hover:text-cream active:scale-95"
            >
              Meet the squad
            </button>
          </motion.div>

          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1 }} className="mt-10 flex flex-wrap gap-2">
            {badges.map((b) => (
              <li key={b} className="flex items-center gap-1.5 rounded-full bg-white/80 px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider text-forest/80 shadow-sm ring-1 ring-sand">
                <ShieldCheck className="h-3.5 w-3.5 text-leaf" /> {b}
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="relative flex items-center justify-center pb-8 lg:pb-0" style={{ perspective: "1200px" }}>
          <motion.div style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: 8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 70, damping: 14, delay: 0.5 }}
              style={{ translateZ: 0 }}
              className="relative z-20 w-56 rounded-2xl shadow-2xl shadow-forest/25 sm:w-72"
            >
              <motion.img
                src="/products/turmeric.webp"
                alt="Turmy Turmeric Powder packaging"
                className="w-full rounded-2xl"
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>

            <motion.div style={{ x: driftX, y: driftY }} className="absolute -left-16 top-10 z-10 hidden w-36 rounded-xl shadow-xl shadow-forest/15 sm:block lg:-left-24 lg:w-44">
              <motion.img
                src="/products/tomato.webp"
                alt="Mr. Toma Tomato Powder packaging"
                className="w-full rounded-xl"
                animate={{ y: [0, -9, 0] }}
                transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
              />
            </motion.div>

            <motion.div style={{ x: driftX, y: driftY }} className="absolute -right-12 bottom-6 z-10 hidden w-32 rounded-xl shadow-xl shadow-forest/15 sm:block lg:-right-16 lg:w-40">
              <motion.img
                src="/products/moringa.webp"
                alt="Mr. Ringa Moringa Powder packaging"
                className="w-full rounded-xl"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1.6 }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 120, damping: 12, delay: 1.1 }}
              className="absolute -bottom-4 -left-6 z-30 h-24 w-24 sm:-left-10"
            >
              <div className="relative h-full w-full animate-spin-slow">
                <svg viewBox="0 0 100 100" className="h-full w-full">
                  <defs>
                    <path id="circlePath" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
                  </defs>
                  <circle cx="50" cy="50" r="50" fill="#0D3220" />
                  <text fill="#FAF7F2" className="font-mono" style={{ fontSize: "9.5px", letterSpacing: "2.5px" }}>
                    <textPath href="#circlePath">EAT REAL · FEEL GOOD · PURE POWER ·</textPath>
                  </text>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-display text-lg font-extrabold text-leaf">12</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
